import useIsMobile from "@/hooks/useIsMobile";
import dynamic from "next/dynamic";

const IplPage = dynamic(() => import("@/screens/IplPage"));
const PageLayout = dynamic(() => import("@/screens/Layout/PageLayout"));

export default function Category({
  categoryNewsListData = null,
  latestStoryData = null,
  latestVideoData = null,
  faqData = null,
  matchData = null,
  pointsData = null,
  mostRunData = null,
  mostWicketsData = null,
}) {
  const isMobileDevice = useIsMobile();
  return (
    <>
      <PageLayout
        rightSideBarData={categoryNewsListData}
        latestStoryData={latestStoryData}
        latestVideoData={latestVideoData}
        isMobileDevice={isMobileDevice}
      >
        <IplPage
          categoryNewsListData={categoryNewsListData}
          isMobileDevice={isMobileDevice}
          faqData={faqData}
          matchData={matchData}
          pointsData={pointsData}
          mostRunData={mostRunData}
          mostWicketsData={mostWicketsData}
        />
      </PageLayout>
    </>
  );
}

export const getServerSideProps = async (ctx) => {
  const { logger } = await import("@/utils/logging/log-util");
  try {
    const { getDataByUrl } = await import("@/handlers/commonApi");
    const { getLatestStoryVideoFeed, splitLatestStoryVideoFeed } = await import("@/handlers/content");
    const { getFaqBySlug } = await import("@/handlers/faq");
    const { getRedisApiKey, fetchRedisWrapper } = await import(
      "@/handlers/redisKey"
    );
    const {
      getLiveMatches,
      getRecentMatches,
      getUpcomingMatches,
      getPointsTable,
      getMostRuns,
      getMostWickects,
    } = await import("@/handlers/ipl");

    const { parseMatchData } = await import("@/utils/ipl");
    const { setPageTTL } = await import("@/utils/setPageTTL");
    const { getUnixTimeDaysAgo } = await import("@/utils/dateTimeFormat");
    const unixtime = getUnixTimeDaysAgo(15);
    //* get page from query param and subtract 1 from it , also error handle if not a number
    let page = ctx?.query?.page;
    if (isNaN(page)) {
      page = 0;
    }
    page = page ? parseInt(page) - 1 : 0;

    let categoryListRedisKey = getRedisApiKey({
      redisPrefix: "PREFIX_REDIS_ROS",
      params: {
        api: "getdatabyurl",
        url: "/sports",
        page: page,
        display: 6,
        type: "story",
      },
    });

    let faqRedisKey = getRedisApiKey({
      redisPrefix: "PREFIX_REDIS_ROS",
      params: { api: "staticpages", url: "ipl-2025-faqs" },
    });

    const latestStoryVideoRedisKey = getRedisApiKey({
      redisPrefix: "PREFIX_REDIS_ROS",
      params: {
        api: "latestcontentfeed",
        page: 0,
        display: 5,
        type: "story,video",
        unixtime,
      },
    });
    const getMatches = async () => {
      try {
        let matchArr = [];

        // Fetch all data in parallel to reduce load time
        const [liveMatchData, recentMatchData, upcomingMatchData] =
          await Promise.all([
            getLiveMatches(),
            getRecentMatches(),
            getUpcomingMatches(),
          ]);

        // Process Live Matches First (Max 6)
        if (liveMatchData.length) {
          matchArr = liveMatchData.map(parseMatchData);
        }
        // Add Recent Match If Needed
        if (matchArr.length < 6 && recentMatchData?.data?.length) {
          if (upcomingMatchData?.data?.length >= 3) {
            matchArr.push(parseMatchData(recentMatchData?.data[0]));
          } else {
            for (let i = 0; i < 3; i++) {
              if (matchArr.length === 6) break;
              matchArr.push(parseMatchData(recentMatchData.data[i]));
            }
          }
        }

        // Add Upcoming Matches Until 6
        if (matchArr.length < 6 && upcomingMatchData?.data?.length) {
          const upcomingArr = upcomingMatchData?.data;
          for (let i = upcomingArr.length - 1; i >= 0; i--) {
            if (matchArr.length === 6) break;
            matchArr.push(parseMatchData(upcomingArr[i]));
          }
        }
        return matchArr;
      } catch (err) {
        logger.error(err);
      }
    };
    const getPoints = async () => {
      try {
        const pointsData = await getPointsTable();
        if (pointsData) {
          return pointsData.standings?.stage1?.groups[0]?.team?.slice(0, 5);
        }
        return null;
      } catch (err) {
        logger.error(err);
      }
    };
    // get most runs
    const getMostRunsPlayer = async () => {
      try {
        const mostRunData = await getMostRuns();

        if (mostRunData) {
          return mostRunData;
        }
        return null;
      } catch (err) {
        logger.error(err);
      }
    };

    // get most wickets
    const getMostWicketsPlayer = async () => {
      try {
        const mostWicketsData = await getMostWickects();

        if (mostWicketsData) {
          return mostWicketsData;
        }
        return null;
      } catch (err) {
        logger.error(err);
      }
    };
    let [
      categoryNewsListData,
      { latestStoryData, latestVideoData },
      faqData,
      matchData,
      pointsData,
      mostRunData,
      mostWicketsData,
    ] = await Promise.all([
      fetchRedisWrapper(categoryListRedisKey, () =>
        getDataByUrl({
          url: "/sports",
          page: page,
          display: 6,
          type: "story",
        })
      ),
      fetchRedisWrapper(latestStoryVideoRedisKey, () =>
        getLatestStoryVideoFeed({
          page: 0,
          display: 5,
          unixtime,
        })
      ).then(splitLatestStoryVideoFeed),
      fetchRedisWrapper(faqRedisKey, () =>
        getFaqBySlug({
          url: "ipl-2025-faqs",
        })
      ),
      getMatches(),
      getPoints(),
      getMostRunsPlayer(),
      getMostWicketsPlayer(),
    ]);

    setPageTTL(ctx, 120);
    return {
      props: {
        categoryNewsListData,
        latestStoryData,
        latestVideoData,
        faqData,
        matchData,
        pointsData,
        mostRunData,
        mostWicketsData,
      },
    };
  } catch (error) {
    logger.error(error);
    return {
      notFound: true,
    };
  }
};
